import React, { FC, forwardRef, useEffect } from 'react';
import * as S from './styles';

export interface TappingOption {
  index: number;
  used: boolean;
  value: string;
}

/**
 * The current state for the button.
 *
 * Note that this is to be differentiated from the ResponseState in
 * that it refers to the state of the button, not the session.
 * Two adjacent AudioResponseButtons may have different ButtonStates
 * but there can only be one, unified ResponseState value for the session.
 */
export type TappingOptionButtonState = 'green' | 'red' | 'default' | 'orange' | 'yellow';

type TappingOptionButtonProps = TappingOption & {
  buttonState: TappingOptionButtonState;
  disabled?: boolean;
  onClick?: () => void;
  label: string | null;
  showBigger: boolean;
  onVideo?: boolean;
};

type TappingOptionButtonWithRefProps = TappingOption &
  TappingOptionButtonProps & {
    generateAndSetOptionsGrid: () => void;
    isLastOption: boolean;
    onBlur: (event: React.FocusEvent<HTMLButtonElement>) => void;
  };

export const TappingOptionButton: FC<TappingOptionButtonProps> = ({
  buttonState,
  disabled,
  label,
  onClick,
  used,
  value,
  showBigger,
  onVideo = false,
  ...props
}) => (
  <S.Button
    $label={label}
    $buttonState={buttonState}
    $used={used}
    disabled={disabled}
    onClick={onClick}
    $showBigger={showBigger}
    $onVideo={onVideo}
    {...props}
  >
    {value}
  </S.Button>
);

const TappingOptionButtonWithRefComponent: React.ForwardRefRenderFunction<
  HTMLButtonElement,
  TappingOptionButtonWithRefProps
> = (
  {
    buttonState,
    disabled,
    label,
    onClick,
    used,
    value,
    showBigger,
    onVideo = false,
    generateAndSetOptionsGrid,
    onBlur,
    isLastOption,
    ...props
  },
  ref,
) => {
  useEffect(() => {
    if (isLastOption) {
      generateAndSetOptionsGrid();
    }
  }, [isLastOption, generateAndSetOptionsGrid]);

  return (
    <S.Button
      ref={ref as React.RefObject<HTMLButtonElement>}
      $label={label}
      $buttonState={buttonState}
      $used={used}
      tabIndex={used ? -1 : 0}
      disabled={disabled}
      onClick={e => {
        e.preventDefault();
        onClick && onClick();
      }}
      onTouchEnd={
        /* istanbul ignore next */ e => {
          e.preventDefault();
          onClick && onClick();
        }
      }
      onBlur={onBlur}
      $showBigger={showBigger}
      $onVideo={onVideo}
      aria-pressed={used}
      aria-disabled={disabled}
      {...props}
    >
      {value}
    </S.Button>
  );
};

export const TappingOptionButtonWithRef = forwardRef(TappingOptionButtonWithRefComponent);
